import { parseNumber } from "./clipboardGrid";
import { tr, customLabels } from "../i18n";

// AI review fields and their mapping to order-table columns (user-defined "x_*" columns are appended at runtime).
export const AI_FIELDS = [
    { key: "name", target: "blindPos", width: 80 },
    { key: "width", target: "width", width: 80, num: true },
    { key: "height", target: "height", width: 80, num: true },
    { key: "qty", target: "qty", width: 60, num: true },
    { key: "product", target: "ai_type", width: 110, newCol: "col_ai_type" },
    { key: "fabric", target: "ai_fabric", width: 110, newCol: "col_ai_fabric" },
    { key: "color", target: "ai_color", width: 100, newCol: "col_ai_color" },
    { key: "side", target: "type", width: 80 },
    { key: "motor", target: "motor", width: 100 },
    { key: "cable", target: "color", width: 100 },
    { key: "mounting", target: "notes", width: 120 },
    { key: "borring", target: "borring", width: 100 },
    { key: "other", target: "ai_other", width: 160, newCol: "col_ai_other" },
];
export const LOW_CONF = 0.7;
// PDF overlay colours per field type (others grey).
export const AI_BOX_COLORS = { width: "#3b82f6", height: "#22c55e", name: "#f97316", motor: "#a855f7", fabric: "#eab308", side: "#06b6d4", color: "#ec4899" };
export const AI_ERR_COLOR = "#ef4444";
export const aiBoxColor = (k) => AI_BOX_COLORS[k] || "#94a3b8";
export const AI_ASSIGN_KEYS = ["fabric", "width", "height", "name", "motor", "side", "cable", "mounting", "borring", "color", "qty", "product", "other"];
const BASE_N = AI_FIELDS.length, BASE_ASSIGN_N = AI_ASSIGN_KEYS.length;
export const isCustomKey = (k) => String(k).startsWith("x_");
export function setCustomFields(list) {
    AI_FIELDS.splice(BASE_N, AI_FIELDS.length, ...list.map((c) => ({ key: c.key, target: `ai_${c.key}`, width: 140, newCol: true, custom: true, label: c.label })));
    AI_ASSIGN_KEYS.splice(BASE_ASSIGN_N, AI_ASSIGN_KEYS.length, ...list.map((c) => c.key));
    Object.keys(customLabels).forEach((k) => delete customLabels[k]);
    list.forEach((c) => { customLabels[c.key] = c.label; });
}

export const isUncertain = (f) => !!f && !f.edited && (f.conf ?? 0) < LOW_CONF && f.value !== "" && f.value != null;

export function toReviewItems(items) {
    return (items || []).map((s, i) => ({
        _id: s._id ?? i, include: true, page: s.page || 1, errors: s.errors || [],
        block: s.block || "", cont: s.cont || "", group: s.group, pages: s.pages, src: s.src?.length > 1 ? toReviewItems(s.src) : undefined,
        f: Object.fromEntries([...new Set([...AI_FIELDS.map((x) => x.key), ...Object.keys(s).filter(isCustomKey)])].map((key) => {
            const f = { value: key === "qty" ? 1 : "", conf: 0, box: null, ...(s[key] || {}) };
            return [key, { ...f, orig: f.value }];
        })),
    }));
}

// Converts reviewed items into table rows (only chosen columns), plus the new columns that must exist.
export function reviewToRows(items, importCols, uid, existingCols, aid = null) {
    const fields = AI_FIELDS.filter((f) => importCols.includes(f.key));
    const newCols = fields.filter((f) => f.newCol && !existingCols.some((c) => c.key === f.target))
        .map((f) => ({ key: f.target, label: f.custom ? f.label : tr(f.newCol), type: "text", width: f.width + 10, custom: true }));
    const rows = items.map((it) => {
        const r = { id: uid() };
        fields.forEach(({ key, target, num }) => {
            const v = it.f[key]?.value;
            r[target] = num ? parseNumber(v) : String(v ?? "");
        });
        if (r.qty == null || !(r.qty > 0)) r.qty = 1;
        const conf = {}, boxes = {};
        ["name", "width", "height", "qty"].forEach((k) => {
            conf[k] = it.f[k]?.edited ? 1 : (it.f[k]?.conf ?? 0);
            boxes[k] = it.f[k]?.page && it.f[k].page !== it.page ? null : it.f[k]?.box || null;
        });
        r.ai = { page: it.page, boxes, conf, needsReview: AI_FIELDS.some(({ key }) => isUncertain(it.f[key])) };
        if (aid) {
            r.ai_src = { aid, idx: it._id };
            r.ai_orig = Object.fromEntries(fields.map(({ key, target, num }) => [target, num ? parseNumber(it.f[key]?.orig) : String(it.f[key]?.orig ?? "")]));
        }
        return r;
    });
    return { rows, newCols };
}
